import { Button, Card, Checkbox, Typography } from "antd";
import { Fragment, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../loader/loader";
import { addPermission, loadSingleRole } from "./roleApis";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
const PermissionTable = ({ permissions, selectedPermissions, setSelectedPermissions }) => {
  const actionTypes = [
    "Data View",
    "All Data Access",
    "Team Data Access",
    "Create",
    "Update",
    "Delete"
  ];
  const [collapsed, setCollapsed] = useState({});

  const visitPermissionTree = (permission, callback) => {
    if (!permission) {
      return;
    }
    callback(permission);
    (permission.children || []).forEach((child) => visitPermissionTree(child, callback));
  };

  const setPermissionAction = (target, permission, actionType, checked) => {
    if (!permission?.name) {http://localhost:3000/%22https://hrmsapi.softomation.com%22
      return;
    }
    const key = `${permission.name}-${actionType}`.toLowerCase();
    target[key] = checked;

    if (checked && actionType !== "Data View") {
      const dataViewKey = `${permission.name}-data view`.toLowerCase();
      target[dataViewKey] = true;
    }

    if (!checked && actionType === "Data View") {
      ["All Data Access", "Team Data Access", "Create", "Update", "Delete"].forEach((type) => {
        const typeKey = `${permission.name}-${type}`.toLowerCase();
        target[typeKey] = false;
      });
    }
  };

  const applyToNodeAndDescendants = (target, permission, actionType, checked) => {
    visitPermissionTree(permission, (node) => setPermissionAction(target, node, actionType, checked));
  };

  const findPermissionById = (permissionList, id) => {
    for (const perm of permissionList) {
      if (perm.id === id) {
        return perm;
      }
      if (perm.children && perm.children.length) {
        const found = findPermissionById(perm.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const updateParentHierarchy = (parentId, actionType, target) => {
    if (parentId == null) {
      return;
    }
    const parent = findPermissionById(permissions, parentId);
    if (!parent) {
      return;
    }

    const allChildrenChecked = (parent.children || []).every((child) =>
      Boolean(target[`${child.name}-${actionType}`.toLowerCase()])
    );

    setPermissionAction(target, parent, actionType, allChildrenChecked);

    if (actionType !== "Data View" && allChildrenChecked) {
      const dataViewKey = `${parent.name}-data view`.toLowerCase();
      target[dataViewKey] = true;
    }

    if (parent.parentId != null) {
      updateParentHierarchy(parent.parentId, actionType, target);
    }
  };

  const handleHeaderChange = (actionType, checked) => {
    const newPermissions = { ...selectedPermissions };
    permissions.forEach((permission) =>
      applyToNodeAndDescendants(newPermissions, permission, actionType, checked)
    );
    setSelectedPermissions(newPermissions);
  };

const handlePermissionChange = (permission, actionType, checked) => {
	const newPermissions = { ...selectedPermissions };
	applyToNodeAndDescendants(newPermissions, permission, actionType, checked);

	updateParentHierarchy(permission.parentId, actionType, newPermissions);

	setSelectedPermissions(newPermissions);
  };

const toggleCollapse = (permissionId) => {
    setCollapsed(prev => ({
      ...prev,
      [permissionId]: !prev[permissionId]
    }));
  };

  const renderPermissionRow = (permission, level = 0) => {
    // Increase the indentation factor for better visual separation
    const indent = level * 40; // Increased from 30 to 40
    const hasChildren = permission.children && permission.children.length > 0;
    const isCollapsed = collapsed[permission.id];
    const isParent = level === 0;

    const rows = [
      <tr 
        key={permission.id ?? permission.name} 
        className={`border-b ${isParent ? 'bg-gray-100' : ''}`}
      >
        <td className="p-3 flex items-center" style={{ paddingLeft: `${indent + 16}px` }}>
          {hasChildren ? (
            <span onClick={() => toggleCollapse(permission.id)} className="flex items-center cursor-pointer">
              {isCollapsed ? <RightOutlined className="mr-2" /> : <DownOutlined className="mr-2" />}
              <span className={level > 0 ? 'text-gray-700' : 'font-medium'}>
                {permission.name}
              </span>
            </span>
          ) : (
            <span className={`${level > 0 ? 'text-gray-700 ml-6' : 'font-medium'}`}>
              {permission.name}
            </span>
          )}
        </td>
        {actionTypes.map(type => (
          <td key={`${permission.name}-${type}`} className="p-3 text-center">
            <Checkbox
              checked={selectedPermissions[`${permission.name}-${type}`.toLowerCase()]}
              onChange={(e) => handlePermissionChange(permission, type, e.target.checked)}
            />
          </td>
        ))}
      </tr>
    ];

    if (hasChildren && !isCollapsed) {
      permission.children.forEach((child, index) => {
        const isLastChild = index === permission.children.length - 1;
        const childRows = renderPermissionRow(
          { ...child, parentId: child.parentId ?? permission.id },
          level + 1
        );
        childRows.forEach((row, rowIndex) => {
          if (isLastChild && rowIndex === childRows.length - 1) {
            rows.push(
              <tr key={`${child.id ?? child.name}-divider`} className="border-b-2 border-gray-300">
                <td colSpan={actionTypes.length + 1}></td>
              </tr>
            );
          }
        });
        rows.push(...childRows);
      });
    } else if (isParent && (!hasChildren || isCollapsed)) {
      rows.push(
        <tr key={`${permission.id ?? permission.name}-divider`} className="border-b-2 border-gray-300">
          <td colSpan={actionTypes.length + 1}></td>
        </tr>
      );
    }

    return rows;
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="p-3 border-b text-left">Menu</th>
            {actionTypes.map(type => (
              <th key={type} className="p-3 border-b text-center">
                <Checkbox
                  checked={permissions.every(perm => 
                    selectedPermissions[`${perm.name}-${type}`.toLowerCase()] &&
                    (!perm.children || perm.children.every(child => 
                      selectedPermissions[`${child.name}-${type}`.toLowerCase()]
                    ))
                  )}
                  onChange={(e) => handleHeaderChange(type, e.target.checked)}
                >
                  {type}
                </Checkbox>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {permissions.map(permission => (
            <Fragment key={permission.name}>
              {renderPermissionRow(permission)}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DetailsRole = () => {
  const navigate = useNavigate();
  const { RoleId } = useParams();
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [loader, setLoader] = useState(false);
  const [role, setRole] = useState(null);

  const location = useLocation();
  const roleName = location.state?.data?.name || role?.name || "Role";

  // Helper function to flatten permissions (including children)
  const flattenPermissions = (perms) => {
    const flatList = [];
    const traverse = (items) => {
      items.forEach((item) => {
        flatList.push(item);
        if (item.children && item.children.length) {
          traverse(item.children);
        }
      });
    };
    traverse(perms);
    return flatList;
  };

  useEffect(() => {
    if (!RoleId) {
      toast.error("No RoleId provided in URL");
      return;
    }

    loadSingleRole(RoleId)
      .then((response) => {
        // console.log("loadSingleRole response:", response);
        if (response && response.data) {
          setRole(response.data);
          const rawPermissionData = Array.isArray(response.data?.menus)
            ? response.data.menus
            : response.data;
          const permissionData = Array.isArray(rawPermissionData) ? rawPermissionData : [];

          const parseMenuId = (value) => {
            const numeric = Number(value);
            return Number.isNaN(numeric) ? value : numeric;
          };

          const parseParentId = (value) => {
            const numeric = Number(value);
            if (Number.isNaN(numeric) || numeric === 0) {
              return null;
            }
            return numeric;
          };

          const mapPermission = (permission, parentId = null) => {
            const currentId = parseMenuId(permission.MenuId);
            const mappedChildren = Array.isArray(permission.children)
              ? permission.children.map((child) => mapPermission(child, currentId))
              : [];

            return {
              name: permission.MenuName,
              id: currentId ?? permission.MenuId,
              parentId,
              children: mappedChildren
            };
          };

          const formattedPermissions = permissionData.map((permission) =>
            mapPermission(permission, parseParentId(permission.ParentId))
          );

          setPermissions(formattedPermissions);

          const initialPermissions = {};
          const populateInitialPermissions = (permissionNode) => {
            const permissionName = permissionNode.MenuName;
            initialPermissions[`${permissionName}-data view`.toLowerCase()] = Boolean(
              permissionNode.DataView
            );
            initialPermissions[`${permissionName}-all data access`.toLowerCase()] = Boolean(
              permissionNode.IsAllDataAccess
            );
            initialPermissions[`${permissionName}-team data access`.toLowerCase()] = Boolean(
              permissionNode.IsTeamDataAccess
            );
            initialPermissions[`${permissionName}-create`.toLowerCase()] = Boolean(
              permissionNode.DataCreate
            );
            initialPermissions[`${permissionName}-update`.toLowerCase()] = Boolean(
              permissionNode.DataUpdate
            );
            initialPermissions[`${permissionName}-delete`.toLowerCase()] = Boolean(
              permissionNode.DataDelete
            );

            if (Array.isArray(permissionNode.children) && permissionNode.children.length) {
              permissionNode.children.forEach(populateInitialPermissions);
            }
          };

          permissionData.forEach(populateInitialPermissions);
          setSelectedPermissions(initialPermissions);
        } else {
          toast.error("No role data returned");
          setRole(null);
        }
      })
      .catch((error) => {
        // console.error("Error loading role:", error.message);
        toast.error("Failed to load role data");
        setRole(null);
      });
  }, [RoleId]);

  const handleSubmit = async () => {
    setLoader(true);
    try {
      // Flatten the permissions array to include both parents and children
      const flatPermissions = flattenPermissions(permissions);

      // Construct the RolePermission array
      const rolePermissions = flatPermissions.map(perm => ({
        MenuId: parseInt(perm.id), // Ensure MenuId is an integer
        DataView: selectedPermissions[`${perm.name}-data view`.toLowerCase()] || false,
        IsAllDataAccess: selectedPermissions[`${perm.name}-all data access`.toLowerCase()] || false,
        IsTeamDataAccess: selectedPermissions[`${perm.name}-team data access`.toLowerCase()] || false,
        DataCreate: selectedPermissions[`${perm.name}-create`.toLowerCase()] || false,
        DataUpdate: selectedPermissions[`${perm.name}-update`.toLowerCase()] || false,
        DataDelete: selectedPermissions[`${perm.name}-delete`.toLowerCase()] || false
      }));

      const data = {
        RoleId: parseInt(RoleId),
        RolePermission: rolePermissions
      };

      // console.log("Data to be posted:", data); // For debugging

      const response = await addPermission(data);
      if (response.message === "success") {
        toast.success("Permissions updated successfully");
        navigate(-1);
      } else {
        toast.error("Failed to update permissions");
      }
    } catch (error) {
      // console.error("Error updating permissions:", error);
      toast.error("Error updating permissions");
    } finally {
      setLoader(false);
    }
  };

  if (!localStorage.getItem("isLogged")) {
    return <Navigate to="/admin/auth/login" replace />;
  }

  return (
    <>
    <div className="p-4">
      
      <Card bordered={false}>
        <Typography.Title level={3} className="mb-6">
          Role & Right Permission
        </Typography.Title>

        {permissions.length > 0 ? (
          <>
            <PermissionTable
              permissions={permissions}
              selectedPermissions={selectedPermissions}
              setSelectedPermissions={setSelectedPermissions}
            />
            <div className="flex justify-end mt-4 gap-2">
              <Button
                size="large"
                onClick={() => navigate(-1)}
                style={{ backgroundColor: "#6c757d", color: "white" }}
              >
                Close
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={handleSubmit}
                loading={loader}
                style={{ backgroundColor: "#28a745" }}
              >
                Save Changes
              </Button>
            </div>
          </>
        ) : (
          <Loader />
        )}
      </Card>
    </div>
    </>
  );
  
};

export default DetailsRole;